/**
 * Main app layout with header and sidebar
 *
 * Note: All data sync hooks (folders, collection, settings, enrichment) are now
 * called from useAppInitialization, not here. This component is purely UI.
 */
import clsx from 'clsx';
import type { ReactElement, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { GiCardRandom, GiHeartBeats } from 'react-icons/gi';
import { IoAlbums, IoClose, IoLibrary, IoMenu, IoSparkles, IoStar } from 'react-icons/io5';
import { PiCardsFill, PiListChecksFill } from 'react-icons/pi';
import { Link, useLocation } from 'react-router-dom';

import UserMenu from '@/features/auth/UserMenu';
import useIsAdmin from '@/features/ai/hooks/useIsAdmin';

export interface AppLayoutProps {
  children: ReactNode;
}

interface NavItem {
  to: string;
  icon: ReactNode;
  label: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', icon: <IoLibrary className="h-5 w-5" />, label: 'Collection' },
  { to: '/starred', icon: <IoStar className="h-5 w-5" />, label: 'Starred' },
  { to: '/lists', icon: <PiListChecksFill className="h-5 w-5" />, label: 'Lists' },
  { to: '/decks', icon: <PiCardsFill className="h-5 w-5" />, label: 'Decks' },
  { to: '/sets', icon: <IoAlbums className="h-5 w-5" />, label: 'Sets' },
  { to: '/draft', icon: <GiCardRandom className="h-5 w-5" />, label: 'Draft' },
  { to: '/companion', icon: <GiHeartBeats className="h-5 w-5" />, label: 'Companion' },
];

const ADMIN_NAV_ITEMS: NavItem[] = [{ to: '/admin/ai-log', icon: <IoSparkles className="h-5 w-5" />, label: 'AI Log' }];

function useNavItems(): NavItem[] {
  const { isAdmin } = useIsAdmin();
  return isAdmin ? [...NAV_ITEMS, ...ADMIN_NAV_ITEMS] : NAV_ITEMS;
}

export default function AppLayout({ children }: AppLayoutProps): ReactElement {
  const navItems = useNavItems();

  return (
    <div className="flex min-h-screen flex-col">
      {/* Header */}
      {/* Safe-area padding keeps the header clear of a translucent status bar and
          landscape cutouts (the viewport uses viewport-fit=cover). */}
      <header className="bg-obsidian/95 border-storm sticky top-0 z-40 border-b pt-[env(safe-area-inset-top)] backdrop-blur-sm">
        <div className="flex h-16 items-center justify-between pr-[max(1rem,env(safe-area-inset-right))] pl-[max(1rem,env(safe-area-inset-left))] sm:pr-[max(1.5rem,env(safe-area-inset-right))] sm:pl-[max(1.5rem,env(safe-area-inset-left))]">
          {/* Mobile menu + Logo */}
          <div className="flex items-center gap-2">
            <MobileNav />
            <Link to="/" className="group flex items-center gap-3">
              <img src="/favicon.svg" alt="MtgCraft" className="h-8 w-8 transition-transform group-hover:scale-110" />
              <span className="font-heading text-pearl text-xl font-semibold tracking-wide">MtgCraft</span>
            </Link>
          </div>

          {/* Desktop navigation */}
          <nav className="hidden items-center gap-1 md:flex">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} icon={item.icon} label={item.label} />
            ))}
          </nav>

          {/* User menu */}
          <UserMenu />
        </div>
      </header>

      {/* Main content, padded clear of landscape cutouts (viewport-fit=cover) */}
      <main className="h-0 flex-auto pr-[env(safe-area-inset-right)] pl-[env(safe-area-inset-left)]">{children}</main>
    </div>
  );
}

function MobileNav(): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navItems = useNavItems();

  // Close menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close menu on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  return (
    <div ref={menuRef} className="relative md:hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="text-silver hover:bg-slate hover:text-pearl flex items-center rounded-lg p-2 transition-colors"
        aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {isOpen ? <IoClose className="h-6 w-6" /> : <IoMenu className="h-6 w-6" />}
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <nav className="border-storm bg-obsidian shadow-void/50 absolute top-full left-0 z-50 mt-2 w-56 rounded-lg border py-1 shadow-xl">
          {navItems.map((item) => (
            <MobileNavLink key={item.to} to={item.to} icon={item.icon} label={item.label} />
          ))}
        </nav>
      )}
    </div>
  );
}

interface NavLinkProps {
  to: string;
  icon: ReactNode;
  label: string;
}

function NavLink({ to, icon, label }: NavLinkProps): ReactElement {
  const location = useLocation();
  const isActive = location.pathname === to || (to !== '/' && location.pathname.startsWith(to));

  return (
    <Link
      to={to}
      className={clsx(
        'flex items-center gap-2 rounded-lg px-3 py-2 transition-colors',
        isActive ? 'bg-slate text-pearl' : 'text-silver hover:text-pearl hover:bg-slate',
      )}
    >
      {icon}
      <span className="hidden text-sm font-medium xl:inline">{label}</span>
    </Link>
  );
}

function MobileNavLink({ to, icon, label }: NavLinkProps): ReactElement {
  const location = useLocation();
  const isActive = location.pathname === to || (to !== '/' && location.pathname.startsWith(to));

  return (
    <Link
      to={to}
      className={clsx(
        'flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors',
        isActive ? 'bg-slate text-pearl' : 'text-silver hover:text-pearl hover:bg-slate',
      )}
    >
      {icon}
      {label}
    </Link>
  );
}
